import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fullBasePath, extractEventParams } from '../../../util/util'
import {
	trackExerciseResults,
	updatePreview,
	autoFillExerciseSets,
	updateExerciseSet,
	setShowDetails,
	updateCopy,
	addEventNote,
	updateComments,
	getEventDetails,
	getEventTypeKey,
	toggleDeleteItem,
	setResultsTracking,
	addEventToFavorites,
	updateEvent,
} from '../../../state/events'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import { selectUserById } from '../../users/usersSlice'
import { useDebounceCallback } from '@react-hook/debounce'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Popover from 'react-bootstrap/Popover'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import { Link } from 'react-router-dom'
import { capitalize, envAwarePost } from '../../../util/util'
import { addZero } from '../../../util/datetime'
import useWindowSize from '../../../hooks/useWindowSize'
import YoutubeModal from '../../YoutubeModal/YoutubeModal'
import ExerciseRecorder from '../../ExerciseRecorder/ExerciseRecorder'
import { setData as setRecorderData } from '../../ExerciseRecorder/slice'
import useCurrentUser from '../../../hooks/useCurrentUser'
import styles from './Routine.module.css'
import { useEvent } from '../../../hooks/events'
import { extractExerciseFromRoutine } from '../../../state/events'
import useWorkoutGuider from '../../../hooks/useWorkoutGuider'
import { useCountDown } from '../../../hooks/timer'
import Linkify from 'react-linkify'
import {update as updateVideoModal} from '../../VideoModal/slice'
import {
	toggleShow as toggleShowWorkoutGuider,
	update as updateWorkoutGuider,
	reset as resetWorkoutGuider,
	selectShowGuider
} from '../../../state/workoutGuiderSlice'
import { isStandardDuration } from '../../../util/datetime'
import { workoutFieldsPriorityMap } from '../../../util/events'
import useShouldShowFeature from '../../../hooks/useShouldShowFeature'
import useSound from 'use-sound'
import beep from '../../../assets/beep.wav'

const TRACKABLE_ITEM_LABEL_MAP = {
	reps:'Reps',
	weight:'Weight',
	time:'Time',
	rest:'Rest',
	notes:'Notes',
	rep_range:'Rep Range',
	resistance:'Resistance',
	incline:'Incline',
	distance:'Distance',
	rpe:'RPE',
	amrap:'Reps'
}

const linkDecorator = (href, text, key) => (
  <a href={href} key={key} target="_blank" rel="noopener noreferrer">
    {text}
  </a>
)

//columns of assigned data should be hidden if no data is present in any 
//of the sets
const hideColumn = (sets, key) => {
	for (let i = 0; i < sets.length; i++) {
		if(key==='rep_range'&&(Boolean(sets[i].assigned.reps)||Boolean(sets[i].assigned.reps_upper_limit))) return false
		if (Boolean(sets[i].assigned[key])||key==='amrap') return false
	}
	return true
}

const isFavorites = () => {
	const urlParams = new URLSearchParams(window.location.search)
	return urlParams.get('filter') === 'favorite'
}

const getFirstExercise = routine => {
	for (var i = 0; i < routine.length; i++) {
		const routineItem = routine[i]
		if(routineItem.type!=='heading'){
			return routineItem.exercises[0]
		}
	}
}

const getTrackablesFromType = type => (
	type === 'exercise' ?
		['sets', 'reps', 'weight', 'rest', 'time', 'notes'] :
		['time', 'resistance', 'distance', 'rest', 'incline', 'notes']
)

/**
 * 
 * Input a group of exercises outputs a datastructure of exercise 
 * columns to show (eg reps,rest,incline). 
 * 
 */
const getVisibleExerciseGroupColumns = exercises => {
	const result = {
		exercise: new Set(),
		cardio: new Set(),
	}
	exercises.forEach(exercise => {
		exercise.tracked_items.forEach(item=>{
			exercise.set_data.forEach(set=>{
				switch(item){
					case "reps":
						if(Boolean(set.assigned.reps)) result[exercise.type].add(item)
						break
					case "rep_range":
						if(Boolean(set.assigned.reps)||Boolean(set.assigned.reps_upper_limit)) result[exercise.type].add(item)
						break
					case "amrap":
						result[exercise.type].add(item)
						break
					default: 
						if(Boolean(set.assigned[item])) result[exercise.type].add(item)
						break
				}
			})
		})
	})

	return result
}

const colors = ['#edf8fa', '#f0f0f0']

const ResultVideoButton = ({ exercise_id, exercise_type, workout_id, setIdx }) => {
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const dispatch = useDispatch()
	//pull data from state required to indicate to clients if comments have been added
	//and to disable the button for trainers if there is not result data
	const workout = useEvent('workout', workout_id)
	const exercise = extractExerciseFromRoutine(workout.routine, exercise_type, exercise_id)
	const set = exercise.set_data[setIdx]

	const resultVideoExists = Boolean(set.result_video_url)
	const videoIsUnseen = !isClient && !Boolean(set.result_video_seen) && resultVideoExists
	const feedbackExists = isClient && Boolean(set.result_video_notes)

	const handleClick = () => {
		if (!isClient && !resultVideoExists) return
		//if trainer mark the video as seen 
		if (!isClient) {
			dispatch(updateExerciseSet({
				update: { result_video_seen: 1 },
				type: exercise_type,
				id: set.id,
				exercise_id,
				workout_id
			}))
		}
		dispatch(setRecorderData(
			{ show: true, exercise_id, exercise_type, workout_id, setIdx }
		))
	}


	return (
		<OverlayTrigger overlay={
			<Tooltip>
				{
					isClient ?
						//client only copy
						(
							feedbackExists ?
								'View feedback' :
								'Record/upload video of yourself completing exercises'
						) :
						//trianer only copy
						(
							resultVideoExists ?
								(
									videoIsUnseen ?
										'Assess new client recording & provide feedback' :
										'View client recording & edit feedback'
								)
								:
								'Client has not created a recording of this exercise'
						)
				}
			</Tooltip>
		}>
			<Button
				className="d-flex align-items-center position-relative"
				variant={
					isClient ?
						(
							feedbackExists ? "dark" : "outline-dark"
						)
						:
						(
							resultVideoExists ?
								videoIsUnseen ? "dark" : "outline-dark"
								:
								"outline-secondary"
						)
				}
				onClick={handleClick}
				size="sm"
			>
				<i className={
					!resultVideoExists && !isClient ?
						"fas fa-video-slash" :
						"fas fa-video"
				} />
			</Button>
		</OverlayTrigger>
	)
}

const EnterResultsButton = ({ trackingResults, toggleResultsTracking, eventType, hasResults }) => (
	<OverlayTrigger overlay={
		<Tooltip>
			{hasResults ? 'View & edit existing' : 'Enter'} Results for {capitalize(eventType)}
			{!hasResults && <small className="d-block">No results yet</small>}
		</Tooltip>
	}>
		<Button
			onClick={() => {
				//if we are not currently displaying event sub items
				//&& we are turning on results tracking then make the 
				//the event sub items visible 
				toggleResultsTracking(!trackingResults)
			}}
			variant={
				hasResults ?
					(trackingResults ? "primary" : "outline-primary") :
					(trackingResults ? "secondary" : "outline-secondary")
			}
			size="sm"
		>
			<i className={hasResults ? "fa fa-clipboard-check" : "fa fa-clipboard"} />
		</Button>
	</OverlayTrigger>
)

const ExerciseFillButton = ({ exerciseGroup }) => {
	const dispatch = useDispatch()
	const handleFill = () => {
		//get workout_id from first exercise in the group
		const { workout_id } = exerciseGroup[0]
		//enable tracking so we can see the results after the fill
		dispatch(setResultsTracking({
			type: 'workout',
			tracking: true,
			id: workout_id
		}))
		//loop over each exercise, mark it complete and fill it's results
		exerciseGroup.forEach( exercise => {
			//mark complete
			dispatch(trackExerciseResults({
				workout_id: exercise.workout_id,
				exercise_id: exercise.exercise_id,
				complete: 1,
				trackable: 'complete',
				type: exercise.type
			}))
			//update the sets
			dispatch(trackExerciseResults({
				workout_id: exercise.workout_id,
				exercise_id: exercise.exercise_id,
				sets: exercise.sets,
				trackable: 'sets',
				type: exercise.type
			}))
			//dispatch api call to update values in each set
			exercise.set_data.forEach(set => {
				const update = {}
				const ignoreKeys = ['notes','reps_upper_limit']
				Object.entries(set.assigned).forEach(([k, v]) => {
					if (!ignoreKeys.includes(k)) update[`result_${k}`] = v
				})
				dispatch(updateExerciseSet({
					exercise_id: exercise.exercise_id,
					workout_id: exercise.workout_id,
					type: exercise.type,
					update,
					id: set.id,
				}))
			})
		})
	}
	return (
		<OverlayTrigger
			placement="top"
			overlay={
				<Tooltip id={`tooltip-top`} >
					Automatically fill all results as assigned
				</Tooltip>
			}
		>
			<Button
				onClick={handleFill}
				variant="outline-primary"
				size="sm"
			>
				Fill
			</Button>
		</OverlayTrigger>

	)
}

const SetFillButton = ({ set, exercise }) => {
	const dispatch = useDispatch()
	const handleFill = () => {
		//get workout_id from first exercise in the group
		const { workout_id } = exercise
		//enable tracking so we can see the results after the fill
		dispatch(setResultsTracking({
			type: 'workout',
			tracking: true,
			id: workout_id
		}))
		//loop over each exercise, mark it complete and fill it's results
		const update = { complete: 1 }
		const ignoreKeys = ['notes','reps_upper_limit']
		Object.entries(set.assigned).forEach(([k, v]) => {
			if (!ignoreKeys.includes(k)) update[`result_${k}`] = v
		})
		dispatch(updateExerciseSet({
			exercise_id: exercise.exercise_id,
			workout_id: exercise.workout_id,
			type: exercise.type,
			update,
			id: set.id,
		}))
	}
	return (
		<OverlayTrigger
			placement="top"
			overlay={
				<Tooltip id={`tooltip-top`} >
					Automatically fill all results for this set as assigned
				</Tooltip>
			}
		>
			<Button
				onClick={handleFill}
				variant="outline-primary"
				size="sm"
			>
				Fill
			</Button>
		</OverlayTrigger>

	)
}

const ExerciseCompleteButton = ({ exercise }) => {
	const dispatch = useDispatch()
	return (
		<OverlayTrigger
			placement="top"
			overlay={
				<Tooltip id={`tooltip-top`} >
					{exercise.complete === 1 ? 'Mark Exercise Incomplete' : 'Mark Exercise Complete'}
				</Tooltip>
			}
		>
			<Button
				onClick={() => {
					dispatch(trackExerciseResults({
						workout_id: exercise.workout_id,
						exercise_id: exercise.exercise_id,
						complete: exercise.complete === 1 ? 0 : 1,
						trackable: 'complete',
						type: exercise.type
					}))
				}}
				variant={
					exercise.complete === 1 ?
						"success" :
						"outline-success"
				}
				size="sm"
			>
				<i className="fa fa-check" />
			</Button>
		</OverlayTrigger>
	)
}

const SetCompleteButton = ({ set, exercise }) => {
	const dispatch = useDispatch()
	return (
		<OverlayTrigger
			placement="top"
			overlay={
				<Tooltip id={`tooltip-top`} >
					{set.complete === 1 ? 'Mark Set Incomplete' : 'Mark Set Complete'}
				</Tooltip>
			}
		>
			<Button
				onClick={() => {
					dispatch(updateExerciseSet({
						exercise_id: exercise.exercise_id,
						update: { complete: set.complete ^ 1 },
						workout_id: exercise.workout_id,
						type: exercise.type,
						id: set.id,
					}))
				}}
				variant={
					set.complete === 1 ?
						"success" :
						"outline-success"
				}
				size="sm"
			>
				<i className="fa fa-check" />
			</Button>
		</OverlayTrigger>
	)
}

const ExerciseOptions = ({ exercise, type }) => {
	const dispatch = useDispatch()
	//demo video
	const [demoVideoShow, setDemoVideoShow] = useState(false);
	const [demoVideo, setDemoVideo] = useState("");

	const openPreview = () => {
		dispatch(updatePreview({
			show: true,
			exercise
		}))

	}

	const shouldShowFeature = useShouldShowFeature()
	return (
		<div>
			{Boolean(exercise.demo_link) &&
				<OverlayTrigger
					placement="top"
					overlay={
						<Tooltip id={`tooltip-left`} >
							Demo Video
						</Tooltip>
					}
				>
					<i className="fas fa-video mr-2"
						style={{ cursor: "pointer" }}
						onClick={(e) => {
							setDemoVideo(exercise.demo_link);
							setDemoVideoShow(true);
							e.stopPropagation();
						}}
					/>
				</OverlayTrigger>

			}
			{Boolean(exercise.preview_src) &&
				<OverlayTrigger
					placement="right"
					overlay={
						<Tooltip id={`tooltip-left`} >
							Click to Expand
							<img
								src={`${exercise.preview_src}`}
								style={{ width: `${exercise.preview_width || 180}px` }}
							/>
						</Tooltip>
					}
				>
					<i onClick={openPreview} style={{ cursor: "pointer" }} className="fas fa-eye mr-2 text-primary"></i>
				</OverlayTrigger>
			}
			{Boolean(exercise.demo_video) && (
				<OverlayTrigger
					placement={"top"}
					overlay={
						<Tooltip
							id={`tooltip-top`}
						>
							Demo Video
						</Tooltip>
					}
				>
					<Button
						size="sm"
						className="text-dark mr-2 fas fa-video"
						variant="link"
						onClick={(e) => {
							dispatch(updateVideoModal({
								show:true,
								src:exercise.demo_video,
								heading:exercise.exercise_db_name
							}))
						}}
					/>
				</OverlayTrigger>
			)}
			{(exercise.type === 'exercise'&&shouldShowFeature('exercise_progress')&&shouldShowFeature('all_progress_data')) && <OverlayTrigger
				placement={"top"}
				overlay={
					<Tooltip
						id={`tooltip-right`}
					>
						Exercise weight/rep history
					</Tooltip>
				}
			>
				<Link to={`/exercise_progress/?client=${exercise.client_id}&exerciseID=${exercise.exercise_db_id}`}>
					<i className="fa fa-chart-line" />
				</Link>
			</OverlayTrigger>
			}
			<YoutubeModal setDemoVideoShow={setDemoVideoShow} demoVideoShow={demoVideoShow} demoVideo={demoVideo} />

		</div>
	)
}

const SupersetExercise = ({ exercise, superset, trackingResults, compactMode, showValueHeadings, visibleColumns }) => {
	return (
		<div className={styles.SupersetExercise}>
			<div className={styles.ExerciseNameOptions}>
				<span className={[
					styles.ExerciseName,
					exercise.type === 'cardio' && styles.Cardio
				].join(' ')}>
					{exercise.exercise_name}
				</span>
				<ExerciseOptions exercise={exercise} />
			</div>
			<div className="d-flex w-100">
				{exercise.type === 'exercise' &&
					<div className={styles.SupersetPlaceholder} />
				}
				{
					compactMode ?
						<CompactSet
							visibleColumns={visibleColumns}
							showValueHeadings={showValueHeadings}
							trackingResults={trackingResults}
							exercise={exercise}
							isSuperset
						/>
						:
						<AssignedSet
							visibleColumns={visibleColumns}
							data={exercise.set_data[superset]}
							trackingResults={trackingResults}
							compactMode={compactMode}
							exercise={exercise}
							idx={superset}
							isSuperset
						/>
				}
			</div>
		</div>
	)
}

const Superset = ({ exercises, toggleResultsTracking, trackingResults, compactMode, hasResults, visibleColumns }) => {
	const dispatch = useDispatch()
	const { filter } = extractEventParams(window.location.search)
	const [windowWidth] = useWindowSize()
	const exercise = exercises[0]
	const handleSetsChange = (newValue) => {
		//update the sets
		dispatch(trackExerciseResults({
			workout_id: exercise.workout_id,
			exercise_id: exercise.exercise_id,
			sets: newValue,
			trackable: 'sets',
			type: exercise.type
		}))
	}
	const extractNumSets = exercises => {
		let numSets = Infinity
		exercises.forEach(e => {
			if (e.set_data.length < numSets) numSets = e.set_data.length
		})
		return numSets
	}
	const debouncedHandleSetsChange = useDebounceCallback(handleSetsChange, 300)
	return (
		<div>
			<div className="d-flex justify-content-between mb-2">
				<div className="d-flex align-items-center">
					<strong>Superset:</strong>
					{trackingResults &&
						<div className="ml-2 d-flex align-items-center">
							<Form.Control
								defaultValue={exercise.result_sets || ''}
								onChange={e => {
									debouncedHandleSetsChange(e.target.value)
								}}
								size="sm"
								style={{ width: '60px' }}
							/>
							<span className="ml-2">/</span>
						</div>
					}
					<span className="ml-2"><span>{exercise.sets}</span> sets</span>
				</div>
				<div className="d-flex align-items-start">
					{!isFavorites() &&
						<ButtonGroup>
							<EnterResultsButton
								trackingResults={trackingResults}
								toggleResultsTracking={toggleResultsTracking}
								eventType={'exercise'}
								hasResults={hasResults}
							/>
						</ButtonGroup>
					}
				</div>
			</div>
			{
				compactMode ?
					exercises.map((exercise, i) => (
						<SupersetExercise
							visibleColumns={visibleColumns}
							key={`compact-superset-exercise-${exercise.exercise_id}`}
							trackingResults={trackingResults}
							exercises={exercises}
							exercise={exercise}
							superset={0}
							showValueHeadings={
								//show value headings (eg reps/weight)
								//if first exercise or if previous exercise
								//is not the same type
								(i === 0) ||
								(exercise.type !== exercises[i - 1].type) ||
								windowWidth < 1000
							}
							compactMode={compactMode}
						/>
					))
					:
					[... new Array(extractNumSets(exercises))].map((_, superset) => (
						<div key={`expanded-superset-exercise-${exercises[0].exercise_id}-${superset}`} className={styles.SupersetGroup}>
							<span className={styles.SupersetNumber}>#{superset + 1}</span>
							<div className="w-100">
								{exercises.map((exercise, i) => (
									<SupersetExercise
										visibleColumns={visibleColumns}
										trackingResults={trackingResults}
										exercises={exercises}
										exercise={exercise}
										superset={superset}
										showValueHeadings={
											//show value headings (eg reps/weight)
											//if first exercise or if previous exercise
											//is not the same type
											(i === 0) ||
											exercise.type !== exercises[i - 1].type
										}
										compactMode={compactMode}
									/>
								))}
							</div>
						</div>
					))
			}
		</div>
	)
}

const clipNotes = n => (
	n.length > 40 ?
		`${n.substr(0, 40).trim()}...` :
		n
)

const AssignedSet = ({ data, exercise, idx, trackingResults, isSuperset, compactMode, visibleColumns }) => {
	const ref = useRef()
	const dispatch = useDispatch()
	const [windowWidth] = useWindowSize()
	const { set_id: activeSetId } = useWorkoutGuider()
	const isActive = data.id === activeSetId
	useEffect(() => {
		if (isActive) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
	}, [isActive])
	//README: to prevent sending api calls with every key stroke we need to be 
	//able debounce the inputs BUT we also need to be able to control the inputs
	//from other components like the fill button. To do this we have a local
	//non-debounced value that controls the component and a debounced callback
	//that controls the state
	const handleDispatch = (trackable, newValue) => {
		const update_key = `result_${trackable}`;
		dispatch(updateExerciseSet({
			exercise_id: exercise.exercise_id,
			update: { [update_key]: newValue },
			workout_id: exercise.workout_id,
			type: exercise.type,
			id: data.id,
		}))
	}

	const [localValueMap, setLocalValueMap] = useState({ ...data.result })
	useEffect(() => {
		setLocalValueMap({ ...data.result })
	}, [data.result])

	const debouncedHandleDispatch = useDebounceCallback(handleDispatch, 300)

	const handleChange = (trackable, newValue) => {
		setLocalValueMap({ ...localValueMap, [trackable]: newValue })
		debouncedHandleDispatch(trackable, newValue)
	}

	const ValueComponent = ({k,v,idx})=>{
		const [showNotes,setShowNotes] = useState(false)
		const [showCopied,setShowCopied] = useState(false)
		if (k === 'notes' && windowWidth >= 768) {
			const handleCopy = ()=>{
				setShowCopied(true)
				navigator.clipboard.writeText(v).then(function() {
					console.log('Copying to clipboard was successful!');
				}, function(err) {
					console.error('Could not copy text: ', err);
				});
				setTimeout(setShowCopied.bind(null,false),3000)
			}
			return (Boolean(v) &&
				<>
					<Modal 
						show={showNotes} 
						onHide={setShowNotes.bind(null,false)}
						size="lg"
					>
						<Modal.Header closeButton>
							Exercise Notes
						</Modal.Header>
						<Modal.Body>
							<Button 
								onClick={handleCopy} 
								variant="outline-primary"
								block
								size="sm"
								className="mb-2"
							>
								{showCopied?'Copied':'Copy notes'}
							</Button>
							<div style={{whiteSpace:'pre-line'}}>
								<Linkify componentDecorator={linkDecorator}>
									{v}
								</Linkify>
							</div>
						</Modal.Body>
					</Modal>
					<OverlayTrigger overlay={
						<Tooltip>Click to expand</Tooltip>
					}>
						<div 
							onClick={setShowNotes.bind(null,true)}
							style={{
								cursor: 'pointer',
								borderTop: (idx !== 0 && '1px solid #eee')
							}}
						>
							{clipNotes(v)}
						</div>
					</OverlayTrigger>
				</>
			)
		}
		if (k==='amrap') {
			return (
				<span className="pt-1">
					AMRAP
				</span>
			)
		}
		if (k==='rep_range') {
			return (
				<span className="pt-1">
					{data.assigned.reps}-{data.assigned.reps_upper_limit}
				</span>
			)
		}
		return (
			<span className="pt-1">
				{v}
			</span>
		)
	}

	const numInputs = exercise.tracked_items.length
	return (
		<div
			ref={ref}
			className="w-100 d-flex"
			style={{
				scrollMarginBottom: "300px",
				background: isActive ? '#f0faf1' : '#fff',
				padding: isActive ? '8px 0' : 0,
				marginTop: (idx === 0 || isSuperset) ? '25px' : 0
			}}
		>
			<div className="d-flex w-100 justify-content-between flex-wrap">
				{//map over all non-notes values
					exercise.tracked_items.map(k=>([k,data.assigned[k]]))
						.map(([k, v]) => (
							//for mobile notes have a special component
							//and display underneath all content for non supersets
							((k !== 'notes' || windowWidth >= 768 || isSuperset) && (visibleColumns[exercise.type].has(k) || trackingResults)) &&
							<div style={{
								visibility: (
									hideColumn(exercise.set_data, k) && !trackingResults ?
										'hidden' : 'visible'
								),
								width: (
									k === 'notes' && windowWidth < 768 ?
										'100%' :
										`${100 / numInputs}%`
								),
								marginTop: k === 'notes' && windowWidth < 768 ? '20px' : 0,
								position: 'relative'
							}}>
								{(idx === 0 || isSuperset) &&
									<div>
										<span style={{
											width: '100%',
											display: 'inline-block',
											position: 'absolute',
											top: 0,
											left: 0,
											transform: 'translateY(-100%)',
											marginBottom: '2px',
											borderBottom: '1px solid #dce7f7',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											overflow: 'hidden'
										}}>
											{TRACKABLE_ITEM_LABEL_MAP[k]}
										</span>
									</div>
								}
								<div className={
									k !== 'notes' && (
										windowWidth < 1000 ?
											"d-flex align-items-center flex-column" :
											"d-flex align-items-center"
									)
								}>
									{trackingResults &&
										<span className="d-flex align-items-center">
											<Form.Control
												value={localValueMap[['rep_range','amrap'].includes(k)?'reps':k] || ''}
												placeholder={k}
												onChange={e => {
													handleChange(['rep_range','amrap'].includes(k)?'reps':k, e.target.value)
												}}
												as={k === 'notes' ? 'textarea' : 'input'}
												style={{
													width: k === 'notes' ? '100%' : '60px'
												}}
												size="sm"
											/>
											{(k !== 'notes' && windowWidth >= 1000) && <span className="mx-1">/</span>}
										</span>
									}
									<ValueComponent 
										k={k}
										v={v}
										idx={idx}
									/>
								</div>
							</div>
						))
				}
			</div>
			{(!compactMode && !isFavorites()) &&
				<div
					className="ml-2"
				>
					{
						//for small screens button options will display in a popover 
						windowWidth < 768 ?
							<OverlayTrigger placement="left" trigger="click" rootClose overlay={
								<Popover>
									<Popover.Content>
										<ButtonGroup className="mb-2" size="sm">
											{
												//only show stop watch button if times are valid
												(
													isStandardDuration(exercise.set_data[idx].assigned.rest) ||
													isStandardDuration(exercise.set_data[idx].assigned.time)
												)
												&&
												<ActivateStopwatchButton
													exercise_id={exercise.exercise_id}
													workout_id={exercise.workout_id}
													exercise_type={exercise.type}
													set_id={exercise.set_data[idx].id}
													compactMode={false}
													setIdx={idx}
												/>
											}
											<ResultVideoButton
												exercise_id={exercise.exercise_id}
												workout_id={exercise.workout_id}
												exercise_type={exercise.type}
												setIdx={idx}
											/>
											<SetCompleteButton exercise={exercise} set={data} />
											<SetFillButton exercise={exercise} set={data} />
										</ButtonGroup>
									</Popover.Content>
								</Popover>
							}>
								<Button className="mb-2" variant="outline-dark" size="sm">
									<i className="fa fa-ellipsis-h" />
								</Button>
							</OverlayTrigger>
							:
							<ButtonGroup className="mb-2" size="sm">
								{
									//only show stop watch button if times are valid
									(
										isStandardDuration(exercise.set_data[idx].assigned.rest) ||
										isStandardDuration(exercise.set_data[idx].assigned.time)
									)
									&&
									<ActivateStopwatchButton
										exercise_id={exercise.exercise_id}
										workout_id={exercise.workout_id}
										exercise_type={exercise.type}
										set_id={exercise.set_data[idx].id}
										compactMode={false}
										setIdx={idx}
									/>
								}
								<ResultVideoButton
									exercise_id={exercise.exercise_id}
									workout_id={exercise.workout_id}
									exercise_type={exercise.type}
									setIdx={idx}
								/>
								<SetCompleteButton exercise={exercise} set={data} />
								<SetFillButton exercise={exercise} set={data} />
							</ButtonGroup>
					}
				</div>
			}
		</div>
	)
}

const getCompactResultValue = (set_data, trackable) => {
	const results = set_data.map(s => s.result[trackable])
	const resultsSet = new Set(results)
	const uniqueResults = Array.from(resultsSet)
	return (
		uniqueResults.length === 1 ?
			uniqueResults[0] :
			results.join(' ')
	)
}

const getSetDataDeepCopy = exercise => JSON.parse(JSON.stringify(exercise.set_data))

const CompactSet = ({ exercise, showValueHeadings, trackingResults, isSuperset, visibleColumns }) => {
	console.log(visibleColumns)
	const dispatch = useDispatch()
	const [windowWidth] = useWindowSize()
	//README: to prevent sending api calls with every key stroke we need to be 
	//able debounce the inputs BUT we also need to be able to control the inputs
	//from other components like the fill button. To do this we have a local
	//non-debounced value that controls the component and a debounced callback
	//that controls the state

	//create DEEP copy for local state
	const [localSetData, setLocalSetData] = useState(JSON.parse(JSON.stringify(exercise.set_data)))
	useEffect(() => {
		setLocalSetData(JSON.parse(JSON.stringify(exercise.set_data)))
	}, [exercise.set_data])

	const handleDispatch = (trackable, newValue) => {
		const newValues = newValue.split(' ')
		const update_key = `result_${trackable}`;
		const ids = (
			newValues.length === 1 ?
				exercise.set_data.map(s => s.id) :
				exercise.set_data.map(s => s.id).slice(0, newValues.length)
		)
		//if only one value is input we broadcast this update to all assigned
		//sets else we update each set with the corresponding value in the split list
		if (newValues.length === 1) {
			dispatch(autoFillExerciseSets({
				exercise_id: exercise.exercise_id,
				update: { [update_key]: newValue },
				workout_id: exercise.workout_id,
				type: exercise.type,
				ids
			}))
		} else {
			ids.forEach((id, i) => {
				dispatch(updateExerciseSet({
					exercise_id: exercise.exercise_id,
					update: { [update_key]: newValues[i] },
					workout_id: exercise.workout_id,
					type: exercise.type,
					id
				}))
			})//end ids.foreach
		}//end else

	}
	const firstSet = exercise.set_data[0]
	const debouncedHandleDispatch = useDebounceCallback(handleDispatch, 500)

	const handleChange = (trackable, newValue) => {
		//perform local state update so UI changes immediatly
		const newValues = newValue.split(' ')
		let update;
		if (newValues.length === 1) {
			update = getSetDataDeepCopy(exercise).map(s => {
				const setUpdate = JSON.parse(JSON.stringify(s))
				setUpdate.result[trackable] = newValue
				return setUpdate
			})
		} else {
			update = getSetDataDeepCopy(exercise).slice(0, newValues.length).map((s, i) => {
				const setUpdate = JSON.parse(JSON.stringify(s))
				setUpdate.result[trackable] = newValues[i]
				return setUpdate
			})
		}
		setLocalSetData(update)
		//call debounced dispatcher
		debouncedHandleDispatch(trackable, newValue)
	}

	const ValueComponent = ({k,v})=>{
		const [showNotes,setShowNotes] = useState(false)
		const [showCopied,setShowCopied] = useState(false)
		if (k === 'notes' && windowWidth >= 768) {
			const handleCopy = ()=>{
				setShowCopied(true)
				navigator.clipboard.writeText(v).then(function() {
					console.log('Copying to clipboard was successful!');
				}, function(err) {
					console.error('Could not copy text: ', err);
				});
				setTimeout(setShowCopied.bind(null,false),3000)
			}
			return (Boolean(v) &&
				<>
					<Modal 
						show={showNotes} 
						onHide={setShowNotes.bind(null,false)}
						size="lg"
					>
						<Modal.Header closeButton>
							Exercise Notes
						</Modal.Header>
						<Modal.Body>
							<Button 
								onClick={handleCopy} 
								variant="outline-primary"
								block
								size="sm"
								className="mb-2"
							>
								{showCopied?'Copied Successfully':'Copy notes'}
							</Button>
							<div style={{whiteSpace:'pre-line'}}>
								<Linkify componentDecorator={linkDecorator}>
									{v}
								</Linkify>
							</div>
						</Modal.Body>
					</Modal>
					<OverlayTrigger overlay={
						<Tooltip>Click to expand</Tooltip>
					}>
						<div 
							onClick={setShowNotes.bind(null,true)}
							style={{
								cursor: 'pointer'
							}}
						>
							{clipNotes(v)}
						</div>
					</OverlayTrigger>
				</>
			)
		}
		if (k==='amrap') {
			return (
				<span className="pt-1">
					AMRAP
				</span>
			)
		}
		if (k==='rep_range') {
			return (
				<span className="pt-1">
					{firstSet.assigned.reps}-{firstSet.assigned.reps_upper_limit}
				</span>
			)
		}
		return (
			<span className="pt-1">
				{v}
			</span>
		)
	}

	console.log(firstSet)

	const numInputs = exercise.tracked_items.length
	return (
		<div className="w-100 d-flex">
			<div className="d-flex w-100 justify-content-between">
				{//map over all non-notes values
					exercise.tracked_items.map(k=>([k,firstSet.assigned[k]]))
						.map(([k, v]) => (
							//for mobile notes have a special component
							//and display underneath all content for non supersets
							((k !== 'notes' || windowWidth >= 768 || isSuperset) && (visibleColumns[exercise.type].has(k) || trackingResults)) &&
							<div key={`e-${exercise.exercise_id}-${k}`} style={{
								//only make a column have vislble headings/assigned values 
								visibility: (
									hideColumn(exercise.set_data, k) && !trackingResults ?
										'hidden' : 'visible'
								),
								width: (
									k === 'notes' && windowWidth < 768 ?
										'100%' :
										`${100 / numInputs}%`
								)
							}}>
								{showValueHeadings &&
									<div className="p-1">
										<span style={{
											width: '100%',
											display: 'inline-block',
											marginBottom: '2px',
											borderBottom: '1px solid #dce7f7',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
											overflow: 'hidden'
										}}>
											{TRACKABLE_ITEM_LABEL_MAP[k]}
										</span>
									</div>
								}
								<div className={
									k !== 'notes' && (
										windowWidth < 1000 ?
											"d-flex align-items-center flex-column" :
											"d-flex align-items-center"
									)
								}>
									{trackingResults &&
										<span className="d-flex align-items-center">
											<Form.Control
												defaultValue={getCompactResultValue(localSetData, k) || ''}
												placeholder={TRACKABLE_ITEM_LABEL_MAP[k]}
												onChange={e => {
													handleChange(k, e.target.value)
												}}
												as={k === 'notes' ? 'textarea' : 'input'}
												style={{
													width: k === 'notes' ? '100%' : '60px'
												}}
												size="sm"
											/>
											{(k !== 'notes' && windowWidth >= 1000) && <span className="mx-1">/</span>}
										</span>
									}
									<ValueComponent k={k} v={v}/>
								</div>
							</div>
						))
				}
			</div>
			{(isSuperset && !isFavorites()) &&
				//for small screens button options will display in a popover 
				(windowWidth < 768 ?
					<OverlayTrigger placement="left" trigger="click" rootClose overlay={
						<Popover>
							<Popover.Content>
								<ButtonGroup>
									<ResultVideoButton
										exercise_id={exercise.exercise_id}
										workout_id={exercise.workout_id}
										exercise_type={exercise.type}
										setIdx={0}
									/>
									<ExerciseCompleteButton exercise={exercise} />
									<ExerciseFillButton exerciseGroup={[exercise]} />
								</ButtonGroup>
							</Popover.Content>
						</Popover>
					}>
						<Button className="mb-2 mt-auto" variant="outline-dark" size="sm">
							<i className="fa fa-ellipsis-h" />
						</Button>
					</OverlayTrigger>
					:
					<div
						className="ml-2"
						/*
							This logic ensures that the check and fill buttons are always in line with the assigned values.
							23.5 is the height of the labels that appear above the assigned values
						*/
						style={{ paddingTop: showValueHeadings ? '23.5px' : 0 }}
					>
						<ButtonGroup>
							<ResultVideoButton
								exercise_id={exercise.exercise_id}
								workout_id={exercise.workout_id}
								exercise_type={exercise.type}
								setIdx={0}
							/>
							<ExerciseCompleteButton exercise={exercise} />
							<ExerciseFillButton exerciseGroup={[exercise]} />
						</ButtonGroup>
					</div>)
			}
		</div>
	)
}

const MobileNotes = ({ exercise, trackingResults }) => {
	const dispatch = useDispatch()
	const handleChange = (newValue, id) => {
		dispatch(updateExerciseSet({
			exercise_id: exercise.exercise_id,
			update: { result_notes: newValue },
			workout_id: exercise.workout_id,
			type: exercise.type,
			id
		}))
	}
	const debouncedHandleChange = useDebounceCallback(handleChange, 300)

	const [windowWidth] = useWindowSize()
	//check flag if all notes are the same/match
	//if so only use the first set else use all
	const setsForMap = (
		exercise.all_notes_match ?
			exercise.set_data.slice(0, 1) :
			exercise.set_data
	)
	return (
		//only display if on screens less then 768px and if there are actually 
		//notes to display
		(windowWidth < 768 && !(exercise.all_notes_match && setsForMap[0].assigned.notes === '')) &&
		<div>
			<div style={{
				width: '100%',
				display: 'inline-block',
				marginBottom: '2px',
				borderBottom: '1px solid #dce7f7',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				overflow: 'hidden'
			}}>
				Notes
			</div>
			<div>
				{setsForMap.map((set, i) => (
					<div key={set.id} className="mb-1">
						<div>
							<strong>{exercise.all_notes_match ? '' : `Set #${i + 1}: `}</strong><span>{set.assigned.notes}</span>
						</div>
						{trackingResults &&
							<Form.Control
								defaultValue={set.result.notes || ''}
								placeholder="notes"
								onChange={e => {
									debouncedHandleChange(e.target.value, set.id)
								}}
								as="textarea"
								style={{ width: '100%' }}
								size="sm"
							/>
						}
					</div>
				))}
			</div>
		</div>
	)
}

const Exercise = ({ exercise, toggleResultsTracking, trackingResults, compactMode, visibleColumns }) => {
	const dispatch = useDispatch()
	const { filter } = extractEventParams(window.location.search)
	const handleSetsChange = (newValue) => {
		//update the sets
		dispatch(trackExerciseResults({
			workout_id: exercise.workout_id,
			exercise_id: exercise.exercise_id,
			sets: newValue,
			trackable: 'sets',
			type: exercise.type
		}))
	}
	const debouncedHandleSetsChange = useDebounceCallback(handleSetsChange, 300)
	return (
		<div className="d-flex justify-content-center align-items-stretch flex-column">
			<div className="d-flex justify-content-between align-items-start">
				<div className={styles.ExerciseNameOptions}>
					<span className={[
						styles.ExerciseName,
						exercise.type === 'cardio' && styles.Cardio
					].join(' ')}>
						{exercise.exercise_name}
					</span>
					<ExerciseOptions exercise={exercise} />
				</div>
				<div className="d-inline-flex align-items-center">
					{!isFavorites() &&
						<ButtonGroup>
							{compactMode &&
								<ResultVideoButton
									exercise_id={exercise.exercise_id}
									workout_id={exercise.workout_id}
									exercise_type={exercise.type}
									setIdx={0}
								/>
							}
							{compactMode && <ExerciseCompleteButton exercise={exercise} />}

							<EnterResultsButton
								trackingResults={trackingResults}
								toggleResultsTracking={toggleResultsTracking}
								eventType={'exercise'}
								hasResults={exercise.has_results === 1}
							/>

							{compactMode && <ExerciseFillButton exerciseGroup={[exercise]} />}
						</ButtonGroup>
					}
				</div>
			</div>
			<div className="d-flex w-100 justify-content-between flex-column flex-md-row align-items-md-center">
				{exercise.type === 'exercise' &&
					<div style={{ width: '20%' }}>
						<div>sets</div>
						<div className="d-flex align-items-center">
							{trackingResults &&
								<>
									<Form.Control
										defaultValue={exercise.result_sets}
										onChange={e => {
											debouncedHandleSetsChange(e.target.value)
										}}
										style={{ width: '60px' }}
										size="sm"
									/>
									<span className="mx-1">/</span>
								</>
							}
							<span>{exercise.sets}</span>
						</div>
					</div>
				}
				<div style={{
					flex: '1 1 auto'
				}}>
					{
						compactMode ?
							<CompactSet
								visibleColumns={visibleColumns}
								showValueHeadings={true}
								trackingResults={trackingResults}
								exercise={exercise}
							/>
							:
							exercise.set_data.map((data, i) => (
								<AssignedSet
									visibleColumns={visibleColumns}
									key={data.id}
									compactMode={compactMode}
									trackingResults={trackingResults}
									exercise={exercise}
									data={data}
									idx={i}
								/>
							))
					}
					{/* On small screens ALL notes will appear at the bottom */}
					<MobileNotes trackingResults={trackingResults} exercise={exercise} />
				</div>
			</div>
		</div>
	)
}

const ExerciseGroup = ({ group, toggleResultsTracking, trackingResults, compactMode }) => {
	return (
		<div className={styles.ExerciseGroup}>
			{
				group.type === 'superset' ?
					<Superset
						visibleColumns={getVisibleExerciseGroupColumns(group.exercises)}
						toggleResultsTracking={toggleResultsTracking}
						trackingResults={trackingResults}
						exercises={group.exercises}
						compactMode={compactMode}
						hasResults={group.has_results === 1}
					/>
					:
					<Exercise
						visibleColumns={getVisibleExerciseGroupColumns(group.exercises)}
						toggleResultsTracking={toggleResultsTracking}
						trackingResults={trackingResults}
						exercise={group.exercises[0]}
						compactMode={compactMode}
					/>
			}
		</div>
	)
}

const ActivateStopwatchButton = ({ compactMode, workout_id, exercise_id, exercise_type, set_id, setIdx }) => {
	const dispatch = useDispatch()
	const { updateGuider } = useWorkoutGuider()
	const handleClick = () => {
		dispatch(updateGuider({
			exercise_type,
			exercise_id,
			workout_id,
			show: true,
			set_id,
			setIdx,
		}))
	}
	return (
		<OverlayTrigger overlay={
			<Tooltip>
				Open stop watch for this {Boolean(compactMode) ? 'exercise' : 'set'}
			</Tooltip>
		}>
			<Button
				variant="outline-info"
				onClick={handleClick}
			>
				<i className="fa fa-stopwatch" />
			</Button>
		</OverlayTrigger>
	)
}

const RoutineTimer = ({ workout_id, isPlayable }) => {
	const dispatch = useDispatch()
	const [seconds, setSeconds] = useState(0)
	const { exercise, set, setIdx, autoPlay, moveToNextSet, updateGuider, showCompleted, followingExercise } = useWorkoutGuider()
	const [isActive, setIsActive] = useState(autoPlay)
	const workout = useEvent('workout', workout_id)

	const [playBeep] = useSound(beep)

	const toggleAutoPlay = () => {
		const tgtExercise = exercise || getFirstExercise(workout.routine)
		const tgtSet = tgtExercise.set_data[0]
		dispatch(updateWorkoutGuider({
			exercise_id: tgtExercise.exercise_id,
			exercise_type: tgtExercise.type,
			set_id: tgtSet.id,
			autoPlay: true,
			workout_id,
			setIdx: 0
		}))
	}

	const durationToSeconds = duration => {
		if (!duration) return 0
		const split = duration.split(':')
		return (
			split.length === 1 ?
				parseInt(duration) :
				parseInt(split[0]) * 60 + parseInt(split[1])
		)
	}

	//modes: active,rest,idle
	const [mode, setMode] = useState(autoPlay ? 'countdown' : 'idle')
	//need flag so we show the countdown only once
	const [showCountDown, setShowCountDown] = useState(true)
	useEffect(() => {
		if (autoPlay) {
			setMode(showCountDown ? 'countdown' : 'active')
			setIsActive(true)
			setSeconds(0)
		}
	}, [autoPlay, set, setIdx, exercise])

	const toggleTimer = (newMode) => {
		if (newMode === mode) {
			setIsActive(!isActive)
		} else {
			setSeconds(0)
			setMode(newMode)
			setIsActive(true)
		}
	}

	const activeTime = set?.assigned.time
	const restTime = set?.assigned.rest

	const activeTimeSeconds = durationToSeconds(activeTime)
	const restTimeSeconds = durationToSeconds(restTime)

	const showActiveTime = !isNaN(activeTimeSeconds)
	const showRestTime = !isNaN(restTimeSeconds)

	const progressToNextStep = () => {
		if (mode === 'countdown') {
			playBeep()
			setShowCountDown(false)
			toggleTimer('active')
		}
		//transition from active to rest
		else if (mode === 'active' && restTimeSeconds !== 0) {
			playBeep()
			toggleTimer('rest')
			moveToNextSet("upNext")
		}
		//transition to next set in routine or end autoplay if at end
		else if (mode === 'rest' && restTimeSeconds !== 0) {
			playBeep()
			toggleTimer('active')
			moveToNextSet()
		}else if (restTimeSeconds === 0){
			moveToNextSet()
		}
	}

	const getSecondsLimit = () => {
		switch (mode) {
			case 'countdown': return 3
			case 'active': return activeTimeSeconds
			case 'rest': return restTimeSeconds
			default: return 0;
		}
	}
	const secondsLimit = getSecondsLimit()

	const reset = () => {
		setIsActive(false)
		setSeconds(0)
	}

	const handleDisableAutoPlay = () => {
		setIsActive(false)
		setSeconds(0)
		setMode('idle')
		setShowCountDown(true)
		dispatch(updateGuider({ autoPlay: false }))
	}

	useEffect(() => {
		let interval = null;
		if (isActive && seconds < secondsLimit) {
			interval = setInterval(() => {
				setSeconds(seconds => seconds + 1)
			}, 1000)
		} else if ((!isActive && seconds !== 0) || seconds === secondsLimit) {
			clearInterval(interval)
			if (seconds === secondsLimit) {
				if (autoPlay) {
					progressToNextStep()
				} else {
					reset()
				}
			}
		}
		return () => clearInterval(interval)
	}, [isActive, seconds]);

	const formatTime = seconds => {
		const mins = Math.floor(seconds / 60)
		const secs = seconds - mins * 60
		return `${addZero(mins)}:${addZero(secs)}`
	}
	//if workout is completed show a temporary message and then reset the state
	useEffect(() => {
		let timeout = null
		if (showCompleted) {
			timeout = setTimeout(() => {
				dispatch(updateGuider({ showCompleted: false }))
			}, 2000)
		}
		return () => { clearTimeout(timeout) }
	}, [showCompleted])
	if (showCompleted) {
		return (
			<div className="px-2 py-0" style={{ maxWidth: '100vw', overflow: 'hidden' }}>
				<div className="p-3 px-5 border rounded shadow bg-white">
					<div className="text-center text-success" style={{ fontSize: '24px' }}>
						Workout Complete!
					</div>

				</div>
			</div>
		)
	}

	const gifUrl = (
		(mode === 'rest' && followingExercise) 
		? followingExercise?.preview_src 
		: exercise?.preview_src
	)
	const videoUrl = (
		(mode === 'rest' && followingExercise) 
		? followingExercise?.demo_video 
		: exercise?.demo_video
	)
	//standard render
	return (
		<div className="px-2 py-0" style={{ maxWidth: '100vw', overflow: 'hidden' }}>

			<div className="px-2 border rounded shadow bg-white text-center">
				{
					mode === 'countdown' &&
					<strong className="d-block py-3 px-4">
						Starting in: <span>{3 - seconds}</span>
					</strong>
				}
				{
					(Boolean(set) && Boolean(exercise) && mode !== 'countdown') ?
						<>
							{mode == 'rest' ?
								<div className="text-center">
									<small><b> Up Next: </b>
										{followingExercise?.exercise_name !== exercise.exercise_name ?
											followingExercise?.exercise_name :
											`${followingExercise.exercise_name} - set # ${setIdx + 2}`} </small>
								</div> :
								<div className="text-center"><small>{exercise.exercise_name} - set # {setIdx + 1}</small></div>}

							<div
								className={`text-center my-1 ${!isActive && mode !== 'idle' && "text-muted"}`}
								style={{ fontSize: '24px' }}>
								<span>{formatTime(secondsLimit - seconds)}</span>
								<span className="text-center">
									{(!isActive && mode !== 'idle') && <small className="text-muted"> (paused)</small>}
								</span>
								{isActive && mode === 'rest' &&
									<span className="text-center text-success mb-1"
										style={{ fontSize: "1.2rem" }}> <b> (REST) </b>
									</span>}
							</div>

							<div className="text-center" style={{ position: "relative" }}>
								<span className="text-white"
									style={{
										position: "absolute",
										background: "#808080d6",
										padding: "4px",
										fontSize: "1.2rem"
									}}>
									{mode === 'rest' && "Up Next"}
								</span>
								{Boolean(gifUrl)&&
									<img 
										className={styles.previewGif} 
										src={gifUrl}
										width="250" 
									/>
								}
								{(!Boolean(gifUrl)&&Boolean(videoUrl))&&
									<video 
										autoPlay loop
										className={styles.previewGif} 
										src={videoUrl}
										width="250" 
									/>
								}
							</div>
							<div className="text-center mt-1">
								<ButtonGroup size="sm" className="">
									{autoPlay &&
										<Button
											onClick={handleDisableAutoPlay}
											variant="outline-danger"
										>
											<small>
												<i className="fa fa-square text-danger" /> Stop
											</small>
										</Button>
									}
									{(!autoPlay && isPlayable) &&
										<OverlayTrigger overlay={
											<Tooltip>When enabled you will be guided through your workout according the assigned active/rest times</Tooltip>
										}>
											<Button
												onClick={toggleAutoPlay}
												variant="outline-success"
												className={
													Boolean(set) && Boolean(exercise) ?
														'p-1 px-2 text-wrap' :
														"py-3 px-5 text-wrap"
												}
											>
												<small>
													<i className="fas fa-undo-alt" /> Enable auto-play
												</small>
											</Button>
										</OverlayTrigger>
									}
									{showActiveTime &&
										<OverlayTrigger overlay={
											<Tooltip>
												Total time you should be active during this exercise
											</Tooltip>
										}>
											<Button
												className="text-wrap"
												variant={mode === 'active' ? 'primary' : "outline-primary"}
												onClick={() => { toggleTimer('active') }}
											>
												<i className={[
													(isActive && mode === 'active') ? 'fa fa-pause' : 'fas fa-heartbeat',
													'mr-1'
												].join(' ')} />
												Active {formatTime(activeTimeSeconds)}
											</Button>
										</OverlayTrigger>
									}
									{showRestTime &&
										<OverlayTrigger overlay={
											<Tooltip>
												Total time you should rest before starting the next exercise
											</Tooltip>
										}>
											<Button
												className="text-wrap"
												variant={mode === 'rest' ? 'dark' : "outline-dark"}
												onClick={() => { moveToNextSet("upNext"); toggleTimer('rest'); }}
											>
												<i className={[
													(isActive && mode === 'rest') ? 'fa fa-pause' : 'fas fa-bed',
													'mr-1'
												].join(' ')} />
												Rest {formatTime(restTimeSeconds)}
											</Button>
										</OverlayTrigger>
									}
									<OverlayTrigger overlay={
										<Tooltip>
											Stop timer and reset current time
										</Tooltip>
									}>
										<Button className="text-wrap" variant="outline-warning" onClick={reset}>
											Reset
										</Button>
									</OverlayTrigger>
								</ButtonGroup>
							</div>
						</>
						:
						((mode === 'idle' && isPlayable) &&
							<OverlayTrigger overlay={
								<Tooltip>When enabled you will be guided through your workout according the assigned active/rest times</Tooltip>
							}>
								<Button
									onClick={toggleAutoPlay}
									variant="outline-success"
									className={
										Boolean(set) && Boolean(exercise) ?
											'p-1 px-2' :
											"py-3 px-5"
									}
								>
									<small>
										<i className="fas fa-undo-alt" /> Enable auto-play
									</small>
								</Button>
							</OverlayTrigger>
						)
				}
			</div>
		</div>
	)
}

export const FullScreen = () => {
	const [minimizedRoutineTimer, setMinimizedRoutineTimer] = useState(false);
	const dispatch = useDispatch()
	const show = useSelector(selectShowGuider)
	const statusDispatch = useApiStatusDispatch()
	const [completePending, setCompletePending] = useState(false)
	const { workout } = useWorkoutGuider()
	const routine = workout?.routine
	const workout_id = workout.workout_id
	const id = workout_id
	const eventType = 'workout'
	const compactMode = Boolean(workout.compactMode)
	const title = workout.note
	const isPlayable = Boolean(workout.is_playable)
	const handleHide = () => {
		dispatch(resetWorkoutGuider())
	}
	const handleMarkCompleted = () => {
		setCompletePending(true)
		statusDispatch(updateEvent({
			event_type: 'workout',
			event_id: workout_id,
			complete: 1
		}), 'workout', 'edit').then(() => {
			setCompletePending(false)
		})
	}
	const trackingResults = Boolean(workout?.tracking)
	const showDetails = Boolean(workout?.showDetails)
	//const permissions = usePermissions()
	const dispatchGetEventDetails = () => (
		dispatch(getEventDetails({
			event_type: 'workout',
			event_id: workout.workout_id
		}))
	)
	const toggleResultsTracking = () => {
		//if not showing details and enabling
		//results tracking expand routine
		if (!showDetails && !trackingResults) {
			dispatch(setShowDetails({
				type: eventType,
				showDetails: true,
				id
			}))
		}

		const dispatchSetResultsTracking = () => {
			dispatch(setResultsTracking({
				type: eventType,
				tracking: !trackingResults,
				id
			}))
		}

		dispatchSetResultsTracking();

		if (eventType === 'task') return
		//if no event details fetch them for workouts/nutrition
		//then dispatch set results tracking. Else, immediatly set
		//results tracking
		if (!workout.hasDetails) {
			dispatchGetEventDetails().then(()=>{
				dispatchSetResultsTracking()
			})
		} else {
			dispatchSetResultsTracking()
		}
	}
	//make sure to disable full screen if navigating away (eg using back button)
	useEffect(()=>handleHide,[])
	if (!show) return null
	return (
		<div style={{
			position: 'fixed',
			top: 0, left: 0,
			width: '100vw', height: '100vh',
			overflow: 'hidden',
			zIndex: 1100,
			background: '#eee',
			display: 'flex',
			alignItems: 'stretch',
			flexFlow: 'column'
		}}>
			<div
				className="d-flex justify-content-between align-items-center border p-2 bg-white"
				style={{ flex: '0 0 auto' }}
			>
				<strong>{title}</strong>
				<div className="d-flex align-items-center">
					<OverlayTrigger
						overlay={<Tooltip>Collapse</Tooltip>}
						placement="left"
					>
						<Button
							variant="outline-danger"
							onClick={handleHide}
							size="sm"
						>
							<i className="fa fa-compress" /> Close Workout
						</Button>
					</OverlayTrigger>
				</div>
			</div>
			<div style={{
				height: '100%',
				overflow: 'hidden',
				position: 'relative',
			}}>
				<div style={{
					width: '100%',
					height: '100%',
					padding: '8px 8px 400px 8px',
					overflow: 'auto'
				}}>
					{!compactMode &&
						<Alert variant="info" className="text-dark">
							<i className="fa fa-info-circle mr-2" />
							Tips:{isPlayable && ' Enable guided workout mode in the bottom right or '} Click the stop watch icon to open a timer for each exercise.
						</Alert>
					}
					{routine.map(routineItem => (
						routineItem.type==='heading' ? 
						<div 
							style={{
								fontSize:'18px',
								fontWeight:'600',
								textAlign:'center',
								padding:'4px',
								margin:'8px 0',
								background:'#00041c',
								color:'#ccc',
								borderRadius:'5px',
							}} 
							key={routineItem.id}
						>
							<span style={{borderBottom:'1px solid #00ff8c',whiteSpace:'pre-line'}}>
								{routineItem.heading}
							</span>
						</div>
						:
						<ExerciseGroup
							key={`fullscreen-group-${routineItem.exercises[0].exercise_id}`}
							toggleResultsTracking={toggleResultsTracking}
							trackingResults={trackingResults}
							compactMode={compactMode}
							group={routineItem}
						/>
					))}
					<div className="text-muted text-center mt-3"><small>end of workout</small></div>
					<div className="text-center">
						<Button onClick={handleHide} className="bg-white mr-2" variant="outline-danger">
							Close
						</Button>
						<Button
							onClick={handleMarkCompleted}
							disabled={completePending}
							variant="outline-success"
							className="bg-white"
						>
							<span className="mr-1">mark completed</span>
							{completePending && <Spinner animation="border" size="sm" />}
						</Button>
					</div>
				</div>

				<div style={{
					position: 'absolute',
					bottom: 0,
					right: 0,
					left: !minimizedRoutineTimer && 0,
				}}>

					<OverlayTrigger
						overlay={<Tooltip>{minimizedRoutineTimer ? "Expand" : "Collapse"} </Tooltip>}
						placement="right"
					>
						<Button
							variant="outline-primary"
							style={{position:"absolute",left:"10px"}}
							onClick={()=>setMinimizedRoutineTimer(prev=>!prev)}
							size="sm"
						>
							{minimizedRoutineTimer ? <i className="fas fa-expand" />  :
							<i className="fa fa-compress" />}
						</Button>
					</OverlayTrigger>
					<RoutineTimer isPlayable={isPlayable} workout_id={workout_id} />
				</div>
			</div>

		</div>
	)
}

const Routine = ({ filter, routine, trackingResults, client_id, toggleResultsTracking, eventType, compactMode, isPlayable, title, workout_id }) => {
	const dispatch = useDispatch()
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'

	const workout = useEvent('workout', workout_id)
	const showWorkoutGuider = useSelector(selectShowGuider)
	const handleFullscreen = () => {
		dispatch(updateWorkoutGuider({
			workout_id,
			show: true
		}))
	}

	const toggleAutoPlay = (e) => {
		const firstExercise = getFirstExercise(workout.routine)
		const firstSet = firstExercise.set_data[0]
		dispatch(updateWorkoutGuider({
			exercise_id: firstExercise.exercise_id,
			exercise_type: firstExercise.type,
			set_id: firstSet.id,
			autoPlay: true,
			workout_id,
			setIdx: 0,
			show: true
		}))
	}

	return (
		<>
			<div className="mb-2">
				<ButtonGroup size="sm">
					<Button
						onClick={handleFullscreen}
						variant="outline-dark"
					>
						<i className="fa fa-expand" /> Open Full-screen
					</Button>
					{(isPlayable && isClient) &&
						<Button onClick={toggleAutoPlay} variant="outline-primary">
							<i className="fa fa-play" /> Guide me through this workout
						</Button>
					}
				</ButtonGroup>
			</div>
			<div
				className={styles.Routine}
				className="w-100"
			>
				{(trackingResults && compactMode) &&
					<Alert className="text-dark" variant="info" dismissible>
						<i className="fa fa-info-circle mr-2" />
						To save space, this workout is displaying in "compact mode." When filling out results,
						input a single value that applies to all sets OR add a space between each unique value.
					</Alert>
				}
				{!showWorkoutGuider && routine.map( routineItem => (
					routineItem.type==='heading' ? 
					<div 
						style={{
							fontSize:'18px',
							fontWeight:'600',
							textAlign:'center',
							padding:'4px',
							margin:'8px 0',
							background:'#00041c',
							color:'#ccc',
							borderRadius:'5px'
						}} 
						key={routineItem.id}
					>
						<span style={{borderBottom:'1px solid #00ff8c',whiteSpace:'pre-line'}}>
							{routineItem.heading}
						</span>
					</div>
					:
					<ExerciseGroup
						key={`group-${routineItem.exercises[0].exercise_id}`}
						toggleResultsTracking={toggleResultsTracking}
						trackingResults={trackingResults}
						compactMode={compactMode}
						group={routineItem}
					/>
				))}
			</div>
			<ExerciseRecorder />
		</>
	)
}

export default Routine